import React from 'react';
import { motion } from 'framer-motion';

export default function PrivacyPolicy() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <div style={styles.pageContainer}>
      <motion.div 
        style={styles.contentWrapper}
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header Section */}
        <motion.header style={styles.header} variants={itemVariants}>
          <div style={styles.logoMark}>OMNIO</div>
          <h1 style={styles.title}>Privacy Policy</h1>
          <div style={styles.divider}></div>
          <p style={styles.subtitle}>LAST UPDATED • FEB 21, 2026</p>
        </motion.header>

        {/* Intro */}
        <motion.p style={styles.intro} variants={itemVariants}>
          This Privacy Policy explains how <span style={styles.highlight}>Omnio</span> (“Omnio”, “we”, “us”) collects,
          uses, shares, and retains information when you use the Omnio app and related services.
        </motion.p>

        {/* Policy Sections */}
        <Section 
          title="01 / Scope & Definitions" 
          variants={itemVariants}
        >
          This policy applies to the Omnio app and related services (together, “Omnio”).
          “Ted” is an <span style={styles.highlight}>AI-powered software system</span> within Omnio that helps generate
          in-app responses, plans, and summaries based on information you provide and your app activity. Omnio is responsible
          for all data processing described in this policy. “Modules” may include workouts and physical progress, meals and
          food systems, habit/behavior tracking, shopping/food tools, and the optional AI Therapist.
        </Section>

        <Section 
          title="02 / Data We Collect" 
          variants={itemVariants}
        >
          Depending on what you use, Omnio will collect: account identifiers (such as email and/or phone number),
          device and app diagnostics, usage events, workout selections and performance metrics (for example reps, tempo,
          range of motion, and scores), meal preferences and plans, inventory/stock inputs, content you provide (such as
          text, voice input, and images you upload), and—<span style={styles.highlight}>only if you choose to enable it</span>—location information.
        </Section>

        <Section 
          title="03 / How We Use Data" 
          variants={itemVariants}
        >
          Omnio uses information to provide and operate the app, including generating plans, adapting weekly programs,
          producing reports and progress views, and keeping modules consistent (for example aligning meals with training load
          and inventory). If you enable location, we use it <span style={styles.highlight}>only</span> to suggest products
          based on your location; we do not use location for advertising.
          We also use information for security, fraud prevention, debugging, and service reliability.
          <span style={styles.highlight}>We do not use your personal information for advertising.</span>
        </Section>

        <Section
          title="04 / Workouts, Camera Coach, and Photoday" 
          variants={itemVariants}
        >
          For pose-tracked workouts, Omnio processes camera frames to estimate skeletal pose, rep counts, range of motion,
          and tempo. Omnio aims to retain <span style={styles.highlight}>performance metadata</span> (such as scores and
          workout metrics) and minimize retention of raw camera frames unless you choose to save, export, or share them.
          For Photoday, photos you upload are used to estimate body composition trends and generate comparisons over time.
        </Section>

        <Section 
          title="05 / Voice, Microphone, and “Genius” Mode" 
          variants={itemVariants}
        >
          If you enable voice features, Omnio processes your speech to understand your requests and provide responses.
          Microphone access is used only when you initiate a voice interaction or enable voice-driven features.
          Omnio does not use voice inputs for advertising. If your device or operating system provides voice-to-text,
          that processing may be handled by your device/OS provider.
        </Section>

        <Section 
          title="06 / Meals, Kitchen Scans, and Inventory" 
          variants={itemVariants}
        >
          If you choose to add kitchen items, you can enter them manually, by voice, or by scanning with your camera.
          Images may be analyzed to identify ingredients and estimate quantities to populate Stock and Shopping.
          Inventory and preference signals (such as likes/dislikes) are used to generate meal plans and shopping lists
          based on availability.
        </Section>

        <Section 
          title="07 / Calls, Reminders, and Accountability" 
          variants={itemVariants}
        >
          If you provide a phone number and enable accountability features, Omnio may place a reminder call when a
          scheduled workout is missed. Phone numbers are used for authentication and accountability features, not marketing.
          Call delivery may be handled by telecommunications providers acting as service vendors.
        </Section>

        <Section 
          title="08 / AI Therapist (Optional Paid Feature in future releases, currently completely free)" 
          variants={itemVariants}
        >
          The AI Therapist is an optional paid feature (the first session may be available at no additional charge).
          It may use your broader Omnio context (such as consistency, workload, and behavior patterns) together with the
          content of sessions to provide reflections and support. We do not sell Therapist session content, and we do not
          use it for advertising.
        </Section>

        <Section 
          title="09 / Sharing, Service Providers and Payments" 
          variants={itemVariants}
        >
          Omnio may share information with service providers that help us operate the app (for example hosting, storage,
          analytics, customer support and communications) under contractual obligations.
        </Section>

        <Section 
          title="10 / Data Retention, Deletion, and Your Controls" 
          variants={itemVariants}
        >
          Omnio retains information for as long as needed to provide the service and maintain your history (such as plans,
          reports, and progress trends), unless you delete it or request deletion where applicable. You can control app
          permissions (such as camera, microphone, and location), update preferences, and manage account access. Some information may be
          retained longer when necessary for security, legal compliance, or dispute resolution.
        </Section>

        <Section 
          title="11 / Security" 
          variants={itemVariants}
        >
          Omnio uses administrative, technical, and physical safeguards designed to protect your information. No system is
          perfectly secure. We work to reduce risk through access controls, encryption in transit where appropriate, and
          monitoring for abuse.
        </Section>

        <Section 
          title="12 / Changes & Contact" 
          variants={itemVariants}
        >
          Omnio may update this policy as the app changes. We will post the updated version and revise the “Last Updated” date.
          For privacy questions or requests, contact <span style={styles.highlight}>omnioplay@gmail.com</span>.
        </Section>

        {/* Footer */}
        <motion.footer style={styles.footer} variants={itemVariants}>
          <p>OMNIO</p>
          <p style={{ opacity: 0.5 }}>© 2026</p>
        </motion.footer>

      </motion.div>
    </div>
  );
};

function Section({ title, children, variants }) {
  return (
  <motion.div 
    style={styles.section} 
    variants={variants}
    whileHover={{ x: 10, backgroundColor: 'rgba(255,255,255,0.03)' }} // Subtle interactive slide
    transition={{ type: 'tween', duration: 0.3 }}
  >
    <h3 style={styles.sectionTitle}>{title}</h3>
    <div style={styles.text}>{children}</div>
  </motion.div>
  );
}

const styles = {
  pageContainer: {
    backgroundColor: '#050505', // "Luxury" black, slightly softer than #000
    color: '#F5F5F5', // Off-white for better readability
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    display: 'flex',
    justifyContent: 'center',
    padding: '80px 24px',
  },
  contentWrapper: {
    maxWidth: '700px',
    width: '100%',
  },
  header: {
    marginBottom: '80px',
  },
  logoMark: {
    fontSize: '12px',
    fontWeight: '700',
    letterSpacing: '4px',
    color: '#444',
    marginBottom: '20px',
  },
  title: {
    fontSize: '3.5rem',
    fontWeight: '800',
    letterSpacing: '-1.5px',
    margin: '0 0 20px 0',
    background: 'linear-gradient(to bottom right, #fff, #888)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  divider: {
    height: '1px',
    width: '60px',
    backgroundColor: '#333',
    marginBottom: '20px',
  },
  subtitle: {
    fontSize: '11px',
    letterSpacing: '2px',
    color: '#666',
    textTransform: 'uppercase',
  },
  intro: {
    fontSize: '1.25rem',
    lineHeight: '1.6',
    color: '#999',
    marginBottom: '80px',
    fontWeight: '400',
  },
  section: {
    marginBottom: '40px',
    padding: '20px',
    borderLeft: '1px solid #333', // Minimalist clean border
    cursor: 'default',
  },
  sectionTitle: {
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#fff',
    marginBottom: '12px',
    fontWeight: '600',
  },
  text: {
    fontSize: '1rem',
    lineHeight: '1.7',
    color: '#888',
    margin: 0,
  },
  highlight: {
    color: '#fff',
    fontWeight: '500',
  },
  footer: {
    marginTop: '100px',
    borderTop: '1px solid #222',
    paddingTop: '30px',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '10px',
    letterSpacing: '1px',
    color: '#444',
    textTransform: 'uppercase',
  }
};
